/**
 * cmTableStyle
 *
 * Unified helpers for the CM-table "styling field" convention shared by point,
 * polygon and linestring tables.
 *
 * Convention (all geometries):
 *   - A single field drives per-feature styling. Its value lives in `row_data`
 *     and is flattened by the backend into `feature.properties`.
 *   - The style expression (`map_display_config.style`) selects the icon/color
 *     via a MapLibre `["match", ["get", <key>], value, output, ..., default]`.
 *   - The relevant style property depends on geometry:
 *       point       → `iconStyle` (icon image path)
 *       polygon     → `fill-color` (color)
 *       linestring  → `line-color` (color)
 *
 * The style expression itself is the source of truth for BOTH the styling key
 * (the `["get", <key>]`) and the selectable choices (the match arms), so the
 * form never has to guess from `field_schema` (whose field name may differ from
 * the styled key).
 */

export type CmSpatialType = "point" | "multipoint" | "polygon" | "multipolygon" | "linestring" | "multilinestring" | "none";

/** A single selectable styling choice derived from a style `match` expression. */
export interface CmStyleChoice {
  /** The value stored in `row_data[<key>]` (e.g. "Camp", "Park"). */
  value: string;
  /** Icon image path for point tables (e.g. "icons/Camp.png"). */
  icon?: string;
  /** Color for polygon/linestring tables (e.g. "#22c55e"). */
  color?: string;
  /** Polygon outline colour. Per-choice — real styles vary it between rules. */
  outlineColor?: string;
  /** Line width, for linestring tables. */
  width?: number;
}

/** The default styling field key when a style has no explicit `["get", key]`. */
export const DEFAULT_STYLE_FIELD = "type";

/**
 * A table's icon size, as the editor models it.
 *
 * MapLibre's `icon-size` is a multiplier on the source image, so a 512px PNG at
 * size 1 covers the screen. Every real CM-table point style therefore scales it
 * by zoom — `["step", ["zoom"], 0.1, 8, 0.2, 12, 0.3]` means 0.1 below zoom 8,
 * 0.2 from 8 to 12, and 0.3 from 12 up. Size is table-level, not per category:
 * the legacy model had one `map_display_config.iconSize` for the whole table and
 * migrated styles repeat the same expression on every rule.
 */
export interface CmIconSize {
  /** False writes a plain number; true writes a `["step", ["zoom"], …]`. */
  zoomScaled: boolean;
  /** The fixed size, or the size below the first breakpoint. */
  base: number;
  /** Breakpoints in ascending zoom order. */
  stops: Array<{ zoom: number; size: number }>;
}

/**
 * What a new point table gets. Deliberately the same shape long-standing org
 * styles use — without a `size` MapLibre defaults `icon-size` to 1 and every
 * marker renders at full image size at every zoom.
 */
export const DEFAULT_ICON_SIZE: CmIconSize = {
  zoomScaled: true,
  base: 0.1,
  stops: [
    { zoom: 8, size: 0.2 },
    { zoom: 12, size: 0.3 }
  ]
};

/**
 * Highest zoom the size editor offers. MapLibre's own ceiling is 24; we stop at
 * 23 so the last band always has somewhere to end.
 */
export const MAX_ICON_ZOOM = 23;

/**
 * One row of the size editor: "from this zoom to that zoom, draw icons this big".
 *
 * A MapLibre `step` expression is really a list of contiguous bands, but stores
 * only the boundaries — so `endZoom` is always the next band's `startZoom` (or
 * {@link MAX_ICON_ZOOM} for the last). Presenting whole bands is far easier to
 * read than a bare list of breakpoints.
 */
export interface CmIconSizeBand {
  startZoom: number;
  endZoom: number;
  size: number;
}

/** Expands a size into contiguous editor bands. Always returns at least one. */
export function iconSizeToBands(iconSize: CmIconSize, maxZoom = MAX_ICON_ZOOM): CmIconSizeBand[] {
  const stops = iconSize.zoomScaled ? [...iconSize.stops].sort((a, b) => a.zoom - b.zoom) : [];
  const boundaries = stops.map((s) => s.zoom);
  const sizes = [iconSize.base, ...stops.map((s) => s.size)];

  return sizes.map((size, i) => ({
    startZoom: i === 0 ? 0 : boundaries[i - 1],
    endZoom: i < boundaries.length ? boundaries[i] : maxZoom,
    size
  }));
}

/** Collapses editor bands back into a size. One band means a flat size. */
export function bandsToIconSize(bands: CmIconSizeBand[]): CmIconSize {
  const ordered = [...bands].sort((a, b) => a.startZoom - b.startZoom);
  const base = ordered[0]?.size ?? DEFAULT_ICON_SIZE.base;
  return {
    zoomScaled: ordered.length > 1,
    base,
    stops: ordered.slice(1).map((b) => ({ zoom: b.startZoom, size: b.size }))
  };
}

/** True for a `["step", ["zoom"], base, z, size, …]` expression. */
const isZoomStepExpression = (value: any): value is any[] =>
  Array.isArray(value) && value[0] === "step" && Array.isArray(value[1]) && value[1][0] === "zoom";

/**
 * Reads a symbolizer `size` into {@link CmIconSize}. Anything the editor cannot
 * model — an `interpolate`, a hand-written expression — returns undefined so the
 * caller can leave it untouched rather than flatten it.
 */
export function parseIconSize(size: unknown): CmIconSize | undefined {
  if (typeof size === "number") return { zoomScaled: false, base: size, stops: [] };
  if (!isZoomStepExpression(size)) return undefined;

  const [, , base, ...rest] = size;
  if (typeof base !== "number") return undefined;

  const stops: CmIconSize["stops"] = [];
  for (let i = 0; i < rest.length - 1; i += 2) {
    const zoom = rest[i];
    const stopSize = rest[i + 1];
    if (typeof zoom !== "number" || typeof stopSize !== "number") return undefined;
    stops.push({ zoom, size: stopSize });
  }
  return { zoomScaled: true, base, stops };
}

/** The `size` value to write onto an Icon symbolizer. */
export function buildIconSizeExpression(iconSize: CmIconSize): number | any[] {
  if (!iconSize.zoomScaled || iconSize.stops.length === 0) return iconSize.base;
  const ordered = [...iconSize.stops].sort((a, b) => a.zoom - b.zoom);
  return ["step", ["zoom"], iconSize.base, ...ordered.flatMap((s) => [s.zoom, s.size])];
}

/**
 * The table's current icon size, read from its first Icon symbolizer. Undefined
 * when there is no icon rule yet, or when the stored size is an expression the
 * editor does not model.
 */
export function getIconSize(style: Record<string, any> | undefined): CmIconSize | undefined {
  if (!isGeostylerStyle(style)) return undefined;
  for (const rule of style!.rules ?? []) {
    const sym = (rule?.symbolizers ?? []).find((s: any) => s?.kind === "Icon");
    if (sym) return parseIconSize(sym.size);
  }
  return undefined;
}

/**
 * The GeoJSON property CM-table GeoStyler rules must filter on.
 *
 * The backend serializer flattens a row's `map_display_config[style_key]` into
 * `feature.properties.style_value` — that one name, whatever the table's
 * `style_key` happens to be. So `style_value` is the only property a category
 * rule can usefully test. Mirrors `DEFAULT_FILTER_PROPERTY` in
 * `legacyStyleToGeostyler`, which emits the same thing.
 */
export const STYLE_FILTER_PROPERTY = "style_value";

/**
 * The property a table's GeoStyler rules should filter on, for the *writer*.
 *
 * Distinct from {@link getStyleFieldKey}, whose `"type"` fallback is the row
 * *storage* key for legacy match expressions. Using that as a filter property
 * produces `["==", ["get", "type"], …]`, which no CM-table feature can ever
 * match — the serializer does not emit a `type` property — so every category
 * silently falls through to the default rule.
 *
 * An existing rule's own property is preserved, except the bare `"type"`
 * placeholder: that can only have come from the old fallback and can never
 * match, so it is healed to `style_value` on the next save.
 */
export function getStyleFilterProperty(style?: Record<string, any>): string {
  if (isGeostylerStyle(style)) {
    for (const rule of style!.rules ?? []) {
      const prop = eqFilterProperty(rule?.filter);
      if (prop && prop !== DEFAULT_STYLE_FIELD) return prop;
    }
  }
  return STYLE_FILTER_PROPERTY;
}

/** Returns the base geometry (collapses multi-variants). */
export function baseGeometry(spatialType?: CmSpatialType | string | null): "point" | "polygon" | "linestring" | "none" {
  switch (spatialType) {
    case "point":
    case "multipoint":
      return "point";
    case "polygon":
    case "multipolygon":
      return "polygon";
    case "linestring":
    case "multilinestring":
      return "linestring";
    default:
      return "none";
  }
}

/** Whether this geometry is styled by icon (point) vs color (polygon/line). */
export function isIconStyled(spatialType?: CmSpatialType | string | null): boolean {
  return baseGeometry(spatialType) === "point";
}

/**
 * The style property that carries the per-feature `match` expression for the
 * given geometry: `iconStyle` (point), `fill-color` (polygon), `line-color` (line).
 */
export function styleMatchProperty(spatialType?: CmSpatialType | string | null): "iconStyle" | "fill-color" | "line-color" | null {
  switch (baseGeometry(spatialType)) {
    case "point":
      return "iconStyle";
    case "polygon":
      return "fill-color";
    case "linestring":
      return "line-color";
    default:
      return null;
  }
}

/** True if the value is a MapLibre `["match", ["get", key], ...]` expression. */
function isMatchExpression(expr: any): expr is any[] {
  return Array.isArray(expr) && expr[0] === "match" && Array.isArray(expr[1]) && expr[1][0] === "get";
}

/** True if the value is a stored GeoStyler `Style` (has a `rules` array). */
export function isGeostylerStyle(style: any): boolean {
  return !!style && Array.isArray(style.rules);
}

/** Rule names reserved for fallback + label rules across legacy and backend migration output. */
const RESERVED_RULE_NAMES = new Set(["default", "__default", "__label"]);

/** The value of a simple `["==", prop, value]` GeoStyler filter, else undefined. */
function eqFilterValue(filter: any): string | undefined {
  if (Array.isArray(filter) && filter[0] === "==" && typeof filter[2] === "string" && filter[2]) return filter[2];
  return undefined;
}

/** The property a simple `["==", prop, value]` GeoStyler filter tests, else undefined. */
function eqFilterProperty(filter: any): string | undefined {
  if (Array.isArray(filter) && filter[0] === "==" && typeof filter[1] === "string" && filter[1]) return filter[1];
  return undefined;
}

/**
 * The filter for the fallback rule: `["!", ["||", ==v1, ==v2, …]]` — "every
 * feature none of the categories matched".
 *
 * **A fallback rule must never be left unfiltered.** Every renderer reads "no
 * filter" as *match every feature*, not as *the leftovers*:
 *
 *  - MapLibre gives the rule its own layer with no filter, and rules render in
 *    order, so the last one paints over all the others. A grey fallback fill at
 *    50% opacity then blends over every category colour and the whole layer
 *    reads grey — the symptom reported against the polygon tables.
 *  - QGIS's `RuleRenderer` treats `filter=""` the same way; that path is already
 *    guarded by translating an unfiltered rule to `filter="ELSE"`
 *    (`geostylerToQgis.ts`), which is where this bug was first caught.
 *
 * Every migrated CM-table style in production already stores the negated form
 * (see the fixtures in `__tests__/cmTableStyles.fixture.json`), and
 * `legacyStyleToGeostyler` emits it, so writing it here is what makes an
 * open→save round-trip a genuine no-op rather than a silent downgrade.
 *
 * Returns undefined when there are no categories to fall back *from* — a style
 * whose only rule is the fallback is a plain single-symbol style, and negating
 * an empty set would match nothing at all.
 *
 * GeoStyler models this natively as `Rule.elseRule: true` in geostyler-style 11+;
 * we are pinned at 10, and the stored style is shared with the RN mobile client,
 * so the explicit negation stays the wire format until both can move together.
 */
export function buildElseFilter(fieldKey: string, values: string[]): any[] | undefined {
  const matched = values.filter(Boolean);
  if (!matched.length) return undefined;
  return ["!", ["||", ...matched.map((value) => ["==", fieldKey, value])]];
}

/** Selectable choices read from a stored GeoStyler style's `==`-filtered rules. */
function geostylerChoices(style: Record<string, any>, spatialType?: CmSpatialType | string | null): CmStyleChoice[] {
  const icon = isIconStyled(spatialType);
  const choices: CmStyleChoice[] = [];
  for (const rule of style.rules ?? []) {
    if (RESERVED_RULE_NAMES.has(rule?.name)) continue;
    const value = eqFilterValue(rule?.filter);
    if (!value) continue;
    const sym = (rule?.symbolizers ?? [])[0];
    if (!sym) continue;
    if (icon) {
      if (sym.kind === "Icon") choices.push({ value, icon: String(sym.image ?? "") });
    } else if (sym.kind === "Line") {
      choices.push({ value, color: String(sym.color ?? ""), ...(typeof sym.width === "number" && { width: sym.width }) });
    } else if (sym.kind === "Fill") {
      choices.push({
        value,
        color: String(sym.color ?? ""),
        ...(typeof sym.outlineColor === "string" && sym.outlineColor && { outlineColor: sym.outlineColor })
      });
    }
  }
  return choices;
}

/** The fallback output (icon path / colour) from a GeoStyler style's default rule. */
function geostylerDefaultOutput(style: Record<string, any>, spatialType?: CmSpatialType | string | null): string | undefined {
  const rule = (style.rules ?? []).find((r: any) => r?.name === "default" || r?.name === "__default");
  const sym = rule?.symbolizers?.[0];
  if (!sym) return undefined;
  const out = isIconStyled(spatialType) ? sym.image : sym.color;
  return typeof out === "string" && out ? out : undefined;
}

/**
 * Reads the styling field key from a table's style, e.g. the `type` in
 * `["match", ["get", "type"], ...]`. Falls back to {@link DEFAULT_STYLE_FIELD}.
 */
export function getStyleFieldKey(style: Record<string, any> | undefined, spatialType?: CmSpatialType | string | null): string {
  // GeoStyler rules filter on the serializer's `style_value` property, not the
  // real per-row storage key (e.g. `type`). Callers that write the row should
  // prefer the table's explicit `style_key` (endpoint.styleKey); this filter
  // property is only a last-resort fallback.
  if (isGeostylerStyle(style)) {
    for (const rule of style!.rules ?? []) {
      const prop = eqFilterProperty(rule?.filter);
      if (prop) return prop;
    }
    return DEFAULT_STYLE_FIELD;
  }
  const prop = styleMatchProperty(spatialType);
  const expr = prop ? style?.[prop] : undefined;
  if (isMatchExpression(expr)) {
    const key = expr[1][1];
    if (typeof key === "string" && key) return key;
  }
  return DEFAULT_STYLE_FIELD;
}

/**
 * Parses the selectable choices (value + icon/color) from a table's style
 * `match` expression. Grouped arms (`[["A","B"], output]`) expand to one choice
 * per value. Returns [] when the style has no match expression.
 */
export function getStyleChoices(style: Record<string, any> | undefined, spatialType?: CmSpatialType | string | null): CmStyleChoice[] {
  if (isGeostylerStyle(style)) return geostylerChoices(style!, spatialType);
  const prop = styleMatchProperty(spatialType);
  const expr = prop ? style?.[prop] : undefined;
  if (!isMatchExpression(expr)) return [];

  const icon = isIconStyled(spatialType);
  const choices: CmStyleChoice[] = [];

  // Arms are [value, output] pairs between index 2 and length-1 (last is default).
  for (let i = 2; i < expr.length - 1; i += 2) {
    const rawValue = expr[i];
    const output = expr[i + 1];
    const values = Array.isArray(rawValue) ? rawValue : [rawValue];
    for (const v of values) {
      if (typeof v !== "string") continue;
      choices.push(icon ? { value: v, icon: String(output) } : { value: v, color: String(output) });
    }
  }
  return choices;
}

/** The default (fallback) output — the last element of the match expression. */
export function getStyleDefaultOutput(
  style: Record<string, any> | undefined,
  spatialType?: CmSpatialType | string | null
): string | undefined {
  if (isGeostylerStyle(style)) return geostylerDefaultOutput(style!, spatialType);
  const prop = styleMatchProperty(spatialType);
  const expr = prop ? style?.[prop] : undefined;
  if (!isMatchExpression(expr)) return undefined;
  const last = expr[expr.length - 1];
  return typeof last === "string" ? last : undefined;
}

/**
 * Resolves the icon (point) or color (polygon/line) for a given styling value,
 * falling back to the style's default output when the value has no arm.
 */
export function resolveStyleOutputForValue(
  style: Record<string, any> | undefined,
  spatialType: CmSpatialType | string | null | undefined,
  value: string | undefined | null
): string | undefined {
  const choices = getStyleChoices(style, spatialType);
  const match = value != null ? choices.find((c) => c.value === value) : undefined;
  const output = isIconStyled(spatialType) ? match?.icon : match?.color;
  return output ?? getStyleDefaultOutput(style, spatialType);
}

/** The output (icon path / color) carried by a choice for the given geometry. */
export function choiceOutput(choice: CmStyleChoice, spatialType?: CmSpatialType | string | null): string {
  return (isIconStyled(spatialType) ? choice.icon : choice.color) ?? "";
}

/**
 * Builds a MapLibre `["match", ["get", key], value, output, ..., default]`
 * expression from a flat list of {@link CmStyleChoice}. This is the inverse of
 * {@link getStyleChoices} and keeps one arm per value (no grouping) so the shape
 * round-trips predictably through the editor.
 */
export function buildStyleMatchExpression(
  fieldKey: string,
  choices: CmStyleChoice[],
  defaultOutput: string,
  spatialType?: CmSpatialType | string | null
): any[] {
  const expr: any[] = ["match", ["get", fieldKey]];
  for (const c of choices) {
    if (!c.value) continue;
    expr.push(c.value, choiceOutput(c, spatialType));
  }
  expr.push(defaultOutput);
  return expr;
}

/**
 * Returns a versioned GeoStyler style rebuilt from the editor's choices. This
 * writer deliberately never reintroduces legacy MapLibre match expressions.
 *
 * Three things it must not do, because the editor cannot show them and silently
 * losing them corrupts a working style:
 *  - drop rules it does not manage (a `__label` text rule draws every feature's
 *    name; a hand-written complex filter is someone's deliberate work);
 *  - flatten per-rule symbolizer properties (real polygon styles carry a
 *    different `outlineColor` per category);
 *  - drop unknown top-level keys on the style object.
 *
 * `fieldKey` is the property the rule *filters* on — for backend-migrated styles
 * that is `style_value`, NOT the table's `style_key` (the row storage key). Pass
 * the wrong one and every rule stops matching. See {@link getStyleFieldKey}.
 */
export function buildUpdatedStyle(
  style: Record<string, any> | undefined,
  spatialType: CmSpatialType | string | null | undefined,
  fieldKey: string,
  choices: CmStyleChoice[],
  defaultChoice?: CmStyleChoice,
  /**
   * Table-level icon size, written onto every Icon symbolizer. Omit to keep
   * whatever each rule already had — but a table with no icon rules yet then has
   * no size at all, which MapLibre renders as full-size at every zoom, so the
   * editor always passes one for point geometries.
   */
  iconSize?: CmIconSize
): Record<string, any> {
  const geometry = baseGeometry(spatialType);
  if (geometry === "none") return { ...(style ?? {}) };

  const existingRules: any[] = isGeostylerStyle(style) ? (style!.rules ?? []) : [];
  const isDefaultRule = (rule: any) => rule?.name === "default" || rule?.name === "__default";
  const isCategoryRule = (rule: any) => !RESERVED_RULE_NAMES.has(rule?.name) && !!eqFilterValue(rule?.filter);

  // Everything the editor does not own (`__label`, complex filters) is kept verbatim.
  const preservedRules = existingRules.filter((rule) => !isDefaultRule(rule) && !isCategoryRule(rule));

  const matchesGeometry = (sym: any) => {
    if (geometry === "point") return sym?.kind === "Icon" || sym?.kind === "Mark";
    if (geometry === "linestring") return sym?.kind === "Line";
    return sym?.kind === "Fill";
  };

  // Each category keeps its OWN symbolizer as the base, so per-rule properties
  // survive a round-trip instead of collapsing to the first rule's.
  const sourceByValue = new Map<string, any>();
  for (const rule of existingRules) {
    if (!isCategoryRule(rule)) continue;
    const value = eqFilterValue(rule.filter);
    const sym = (rule.symbolizers ?? []).find(matchesGeometry);
    if (value && sym) sourceByValue.set(value, sym);
  }

  const defaultRuleSymbolizer = (existingRules.find(isDefaultRule)?.symbolizers ?? []).find(matchesGeometry);
  const anySymbolizer = existingRules.flatMap((rule: any) => rule?.symbolizers ?? []).find(matchesGeometry);
  const fallbackSource = defaultRuleSymbolizer ?? anySymbolizer;

  const symbolizerFor = (choice: CmStyleChoice, source: any): Record<string, any> => {
    const base = source ?? fallbackSource;
    const output = choiceOutput(choice, spatialType);
    if (geometry === "point") {
      const icon: Record<string, any> = { ...(base?.kind === "Icon" ? base : {}), kind: "Icon", image: output };
      if (iconSize) icon.size = buildIconSizeExpression(iconSize);
      return icon;
    }
    if (geometry === "linestring") {
      return { ...(base ?? {}), kind: "Line", color: output, width: choice.width ?? base?.width ?? 2 };
    }
    return {
      ...(base ?? {}),
      kind: "Fill",
      color: output,
      opacity: base?.opacity ?? 0.5,
      ...((choice.outlineColor ?? base?.outlineColor) && { outlineColor: choice.outlineColor ?? base?.outlineColor })
    };
  };

  // One rule per category, never two. A second rule for a value already styled
  // is at best dead weight (the first one wins) and at worst fatal: a spatial
  // dataset compiles its rules into ONE `match`-driven layer, and MapLibre
  // rejects a `match` with a repeated branch label — which silently unloads the
  // entire layer. The first choice for a value wins, matching the compiler.
  const seenValues = new Set<string>();
  const categoryChoices = choices.filter((choice) => {
    if (!choice.value || seenValues.has(choice.value)) return false;
    seenValues.add(choice.value);
    return true;
  });

  const rules: Record<string, any>[] = categoryChoices.map((choice) => ({
    name: choice.value,
    filter: ["==", fieldKey, choice.value],
    symbolizers: [symbolizerFor(choice, sourceByValue.get(choice.value))]
  }));

  // The caller may edit the default; otherwise keep whatever the style already had.
  const fallbackOutput = getStyleDefaultOutput(style, spatialType) ?? (isIconStyled(spatialType) ? "" : "#888888");
  const resolvedDefault: CmStyleChoice = defaultChoice ?? {
    value: "",
    ...(isIconStyled(spatialType) ? { icon: fallbackOutput } : { color: fallbackOutput })
  };
  if (choiceOutput(resolvedDefault, spatialType)) {
    // Rebuilt from the CURRENT categories, never carried over from the style being
    // edited: a stale negation would keep excluding a category the user just
    // deleted, leaving those features unpainted by anything. See buildElseFilter
    // for why an unfiltered fallback is never acceptable.
    const elseFilter = buildElseFilter(
      fieldKey,
      categoryChoices.map((choice) => choice.value)
    );
    rules.push({
      name: "default",
      ...(elseFilter ? { filter: elseFilter } : {}),
      symbolizers: [symbolizerFor(resolvedDefault, defaultRuleSymbolizer)]
    });
  }

  return {
    // Unknown top-level keys on an existing GeoStyler style are carried through.
    ...(isGeostylerStyle(style) ? style! : {}),
    format: "geostyler",
    version: 1,
    name: typeof style?.name === "string" ? style.name : "style",
    rules: [...rules, ...preservedRules]
  };
}

/** What {@link mergeCategoryChoices} did, so the UI can report it. */
export interface MergedCategories {
  choices: CmStyleChoice[];
  /** Values that gained a brand-new category row. */
  added: string[];
  /** Values that already had one, left exactly as they were. */
  unchanged: string[];
}

/**
 * Folds a set of category values into the categories a style already has.
 *
 * This is what lets an operator pick a field and get every one of its values
 * styled at once instead of clicking "add category" per value. The merge rules
 * are the whole point of it being a separate, tested function:
 *
 *  - **Nothing already styled is restyled.** A value that already has a category
 *    keeps its icon/colour/outline/width untouched, and keeps its position. Someone
 *    may have spent time on those, and re-running the fill (after new rows arrive,
 *    say) must not undo it.
 *  - **No duplicates.** Values are matched exactly and deduplicated, so re-running
 *    the fill is idempotent. Duplicates already present in `existing` collapse to
 *    the first, since two rules filtering on the same value make the second dead.
 *  - **Nothing is dropped.** Categories whose value is absent from `values` are
 *    kept: the value may simply not appear in the rows scanned yet, and deleting a
 *    working category as a side effect of a lookup would be data loss. Removing one
 *    stays an explicit action.
 *  - **New values are styled by position, continuing past the existing ones**, so a
 *    newly added value never collides with a colour already in use.
 *
 * `styleFor` supplies the appearance for a new value only — it is never consulted
 * for a value that already has a category.
 */
export function mergeCategoryChoices(
  existing: CmStyleChoice[],
  values: string[],
  styleFor: (value: string, index: number) => Partial<CmStyleChoice>
): MergedCategories {
  const deduped: CmStyleChoice[] = [];
  const seen = new Set<string>();
  for (const choice of existing) {
    const value = choice.value?.trim();
    if (!value || seen.has(value)) continue;
    seen.add(value);
    deduped.push(choice);
  }

  const added: string[] = [];
  const unchanged: string[] = [];
  const appended: CmStyleChoice[] = [];

  for (const raw of values) {
    const value = typeof raw === "string" ? raw.trim() : String(raw ?? "").trim();
    if (!value) continue;
    if (seen.has(value)) {
      // Only report it once even if the incoming list repeats it.
      if (!unchanged.includes(value)) unchanged.push(value);
      continue;
    }
    seen.add(value);
    added.push(value);
    // The index continues past everything already styled, so a new value can never
    // be handed a colour one of the reviewed categories is already using.
    appended.push({ value, ...styleFor(value, deduped.length + appended.length) });
  }

  return { choices: [...deduped, ...appended], added, unchanged };
}

/**
 * Returns a new `field_schema` array whose entry for `fieldKey` has its
 * `choices` set to `values` (deduped). If no field matches `fieldKey`, one is
 * appended so the styling field is always represented in the schema.
 */
export function buildUpdatedFieldSchema<T extends { name: string; choices?: string[]; [k: string]: any }>(
  fieldSchema: T[] | undefined,
  fieldKey: string,
  values: string[]
): T[] {
  const uniqueValues = Array.from(new Set(values.filter(Boolean)));
  const schema = Array.isArray(fieldSchema) ? [...fieldSchema] : [];
  const idx = schema.findIndex((f) => f.name === fieldKey);
  if (idx >= 0) {
    schema[idx] = { ...schema[idx], choices: uniqueValues };
  } else {
    schema.push({ name: fieldKey, type: "string", label: fieldKey, choices: uniqueValues } as unknown as T);
  }
  return schema;
}

// ─── Per-row styling (map_display_config on the row) ───────────────────────────
//
// Model: each row carries its own `map_display_config` (a first-class sibling of
// `row_data`) that references a table-level style choice BY VALUE under the
// styling field key, e.g. `{ type: "camp" }`. The table-level style's
// `["match", ["get","type"], …]` expression resolves that value to the row's
// icon/colour, so the row's styling stays in sync with the table config. A row
// with no per-row value falls back to the table default.

/** A single row's style reference, e.g. `{ type: "camp" }`. */
export interface CmRowMapDisplayConfig {
  [key: string]: any;
}

/**
 * Reads a row's selected styling value (e.g. "camp"), stored under the table's
 * styling field key (see {@link getStyleFieldKey}). Returns undefined when the
 * row has no per-row selection.
 */
export function getRowStyleValue(config: CmRowMapDisplayConfig | null | undefined, styleFieldKey: string): string | undefined {
  const value = config?.[styleFieldKey];
  return typeof value === "string" && value ? value : undefined;
}

/**
 * Builds a per-row `map_display_config` that references a table style choice by
 * value, e.g. `{ type: "camp" }`. An empty/nullish value yields `{}` (no per-row
 * override → table default applies).
 */
export function buildRowMapDisplayConfig(styleFieldKey: string, value: string | null | undefined): CmRowMapDisplayConfig {
  if (!value) return {};
  return { [styleFieldKey]: value };
}
